import { movingDocument, otherEscalation } from './shared.js';

/*
 * OmniShield
 *
 * The only brand that can reach a dealership, and the only one whose documents are
 * written against a manual we hold: OSS20 / OSS20AC smoke, rev 12.27.24, plus
 * the Comm Link guide. Everything factual below traces to one of those.
 *
 * ONE CORRECTION TO THE OUTLINE, AND IT MATTERS. The outline branched chirping
 * on "does it stop when you change the battery". These batteries are
 * non-replaceable lithium manganese, sealed for the life of the unit. There is
 * no battery to change, so the question cannot be answered and the branch it
 * guards never resolves. The manual gives a diagnostic that does work: the
 * WARNING icon flashes differently for a dirty chamber, a low battery and
 * end-of-life, and those three lead to genuinely different outcomes. The chirp
 * branch asks what the light is doing instead.
 *
 * See brands/trees/shared.js for how options are worded and what help text is
 * allowed to say.
 */

const omnishield = {
  entry: 'device',
  derive: {
    // "Replace By" is printed on the alarm; ten years is the planning
    // number the replacement conversation is built around.
    pastServiceLife: (answers, facts) => facts.age >= 10
  },
  nodes: {
    device: {
      fields: [{
        autoComplete: 'off',
        help: 'Printed on the label on the back of the sensor. OSS20, OSH20, OSCO20 and so on.',
        id: 'model',
        kind: 'text',
        label: 'Model number',
        required: false
      }, {
        autoComplete: 'off',
        help: 'Depending on the model, it\'s either on the back label or on the side label.',
        id: 'manufactureYear',
        kind: 'year',
        label: 'Year of manufacture',
        required: true
      }],
      help: 'Take one sensor down and read the label on the back. It tells us which unit you have and how old it is, which decides most of what follows.',
      next: 'issue',
      title: 'Which sensor are we looking at?',
      type: 'form'
    },
    issue: {
      options: [{
        icon: 'volume-up-fill',
        label: 'My sensor is chirping',
        next: 'chirp'
      }, {
        icon: 'battery-full',
        label: 'I have a question about the batteries',
        next: 'doc_battery'
      }, {
        icon: 'arrow-repeat',
        label: 'My sensor has reached the 10 year expiration date',
        next: 'doc_service_life'
      }, {
        icon: 'wind',
        label: 'I need to clean my sensor',
        next: 'doc_cleaning'
      }, {
        icon: 'box-seam',
        label: 'I am moving to a new home',
        next: 'doc_moving'
      }, {
        icon: 'cursor-fill',
        label: 'I have a question about the app or the Comm Link',
        next: 'app'
      }, {
        icon: 'card-text',
        label: 'I have a different problem',
        next: 'esc_other'
      }],
      prompt: 'How can we help?',
      type: 'choice'
    },
    /*
     * The diagnostic. All three trouble signals chirp once a minute and
     * sound identical; the WARNING icon is the only thing that separates
     * them, and it separates a five-minute clean from a replacement.
     */
    chirp: {
      help: 'All of the sensors sound the same when they chirp. The WARNING light helps to indicate which unit is chirping and why, so let\'s watch one sensor for a minute.',
      options: [{
        icon: 'wind',
        label: 'It flashes yellow, once every 32 seconds',
        next: 'doc_cleaning'
      }, {
        icon: 'battery-full',
        label: 'It flashes red, once every 32 seconds',
        next: 'doc_low_battery'
      }, {
        icon: 'arrow-repeat',
        label: 'It flashes red twice, once every 32 seconds',
        next: 'doc_end_of_life'
      }, {
        icon: 'search',
        label: 'I cannot see a light at all',
        next: 'esc_chirp'
      }],
      prompt: 'What is the WARNING light doing?',
      type: 'choice'
    },
    doc_cleaning: {
      body: [{
        text: 'Do not use chemical cleaners of any kind. If the sensor is AC powered, turn off the power to that circuit before you take it down.',
        type: 'warning'
      }, 'Dust and dirt collect inside the smoke chamber over time. When enough of it builds up the sensor can false alarm, or the WARNING icon flashes yellow and the horn chirps once every 32 seconds. Cleaning it takes about five minutes.', {
        items: ['Take the sensor off its bracket with a counter-clockwise twist.', 'Gently vacuum all the outside surfaces, or wipe them with a clean dry cloth.', 'Find the black Chamber Cap on the back of the unit and twist it firmly counter-clockwise to remove it.', 'Blow out both the cap and the chamber inside with clean compressed air.', 'Re-install the cap with a firm clockwise rotation, and put the sensor back on its bracket with a clockwise twist.', 'Press and release the Test button. The WARNING light should no longer be flashing yellow.'],
        type: 'steps'
      }, 'If WARNING is still flashing yellow after a clean and a test, the unit needs to come back to us. Do not keep using it.'],
      continue: {
        label: 'I have a different problem',
        next: 'issue'
      },
      escalate: {
        label: 'Get help, it is still flashing',
        reason: 'cleaning_unresolved'
      },
      resolution: 'self_service',
      title: 'Cleaning the smoke chamber',
      type: 'document'
    },
    doc_low_battery: {
      body: [{
        text: 'The battery is a sealed lithium manganese cell and cannot be replaced. A sensor showing low battery has to be replaced as a unit.',
        type: 'warning'
      }, 'One red flash a minute with a chirp means the battery is low. On these sensors that is not something you can fix with a trip to the store.', 'Pressing the Silence button will quiet the chirp for 24 hours, which is enough to get through a night while a replacement is arranged.', 'When the replacement arrives, the old sensor is deactivated with a pin through the small circle on the back and then disposed of. Deactivation is permanent, so only do it to a unit you are throwing away.'],
      continue: {
        label: 'I have a different problem',
        next: 'issue'
      },
      escalate: {
        help: 'The battery cannot be changed and the sensor cannot keep working without it, so a replacement is the only way out of this one. We will sort it out with you.',
        label: 'Arrange a replacement',
        reason: 'low_battery_replacement'
      },
      resolution: 'escalate',
      title: 'Low battery on a sensor',
      type: 'document'
    },
    doc_end_of_life: {
      body: [{
        text: 'A unit in end-of-life cannot be repaired or reset. It has to be replaced.',
        type: 'warning'
      }, 'Two red flashes every 32 seconds with a chirp is the end-of-life signal. The sensor has reached the end of its service life and is telling you so.', 'The Silence button will quiet the chirp for 24 hours while a replacement is arranged.', 'Check the "Replace By" date on the back of your other sensors at the same time. They were almost certainly installed together, which means they will reach this point together.'],
      continue: {
        label: 'I have a different problem',
        next: 'issue'
      },
      escalate: {
        help: 'A unit at end of life cannot be repaired or reset, so there is nothing to try at your end. The next step is arranging the replacement.',
        label: 'Arrange a replacement',
        reason: 'end_of_life_replacement'
      },
      resolution: 'escalate',
      title: 'A sensor has reached end of life',
      type: 'document'
    },
    doc_battery: {
      body: ['Each sensor ships with a sealed, non-replaceable lithium manganese battery sized for the life of the unit. There is no compartment, no nine-volt, and no annual battery change. When the battery is low the sensor is replaced.', 'AC-powered models draw from the circuit and hold the same sealed cell as their backup.', 'A sensor telling you the battery is low flashes WARNING red once every 32 seconds with a chirp. Silence quiets it for 24 hours.'],
      continue: {
        label: 'I have a different problem',
        next: 'issue'
      },
      escalate: {
        help: 'The battery is not a part anybody can change, so a sensor with a run down battery warrants a replacement. We will confirm what you have and sort out the replacement.',
        label: 'Start a replacement request',
        reason: 'battery_question'
      },
      resolution: 'escalate',
      title: 'About the batteries',
      type: 'document'
    },
    doc_service_life: {
      body: ['Every sensor carries a "Replace By" date on its back label. That date is the end of its rated service life, and it is not a suggestion. The detection chemistry degrades whether or not the unit has ever been in an alarm.', 'A sensor at end of life will tell you: WARNING double-flashes red once every 32 seconds with a chirp. It cannot be repaired or reset at that point.', 'Sensors installed at the same time reach that date at the same time, so replacement is usually a whole-network conversation rather than a single unit. It is also the natural moment to look at whether the coverage still matches the house. Rooms change use, and a home with a new bedroom in the basement needs a sensor there.'],
      continue: {
        label: 'I have a different problem',
        next: 'issue'
      },
      escalate: {
        help: 'Sensors past their Replace By date have to be replaced, so this is a conversation rather than a fix. We will go through what you have, what it is protecting, and what it would take to bring it current.',
        label: 'Talk about replacing my sensors',
        reason: 'service_life_replacement'
      },
      resolution: 'escalate',
      title: 'Replacing sensors at ten years',
      type: 'document'
    },
    doc_moving: movingDocument({
      menu: 'issue'
    }),
    app: {
      options: [{
        icon: 'wifi',
        label: 'My Comm Link shows as offline',
        next: 'doc_comm_offline'
      }, {
        icon: 'ethernet',
        label: 'I changed internet providers or routers',
        next: 'doc_comm_setup'
      }, {
        icon: 'plug-fill',
        label: 'I want to switch between Ethernet and WiFi',
        next: 'doc_comm_setup'
      }, {
        icon: 'lock-fill',
        label: 'I cannot sign in to the app',
        next: 'login'
      }, {
        icon: 'phone-fill',
        label: 'I have a new phone and cannot get back in',
        next: 'login'
      }],
      prompt: 'What is happening with the app?',
      type: 'choice'
    },
    doc_comm_offline: {
      body: ['The Comm Link is what carries alerts out of the house. Your sensors still talk to each other and still sound without it, but nobody gets a message. It is worth sorting out.', {
        text: 'Start with the physical checks. Most offline Comm Links are unplugged, moved, or sitting behind a router that changed.',
        type: 'note'
      }, {
        items: ['Check the DC jack is in the Comm Link and the adapter is in a live outlet.', 'If it is on Ethernet, look at the two lights on the port at the back edge. Green stays lit when the cable is properly connected at both ends. Orange flashes when internet data is actually flowing. No green means a cable or a port problem. Green without orange means the cable is fine but the internet is not reaching it.', 'If it is on WiFi, move it to a location that is closer to your router. That will give the Comm Link a stronger signal to work with.', 'Restart your router, then give the Comm Link a couple of minutes to find it again.'],
        type: 'steps'
      }, 'If your internet service, router or WiFi password changed, none of the above will help on its own. The Comm Link needs to be set up again on the new network.'],
      continue: {
        label: 'My internet or router changed',
        next: 'doc_comm_setup'
      },
      escalate: {
        label: 'Get help, it is still offline',
        reason: 'comm_link_offline'
      },
      resolution: 'self_service',
      title: 'Comm Link showing offline',
      type: 'document'
    },
    doc_comm_setup: {
      body: ['A new provider, a new router, a new WiFi password, or a move between WiFi and Ethernet all come to the same thing: the Comm Link has to be pointed at the network again from the app.', {
        text: 'Before you start, find the Serial Number and Security Key on the label on the back of the Comm Link. The letters in the Security Key must be entered in UPPER CASE.',
        type: 'note'
      }, {
        items: ['Open the OmniShield app and start Comm Link setup.', 'Choose WiFi or Ethernet.', 'For Ethernet, run the white cable from the Comm Link to a spare port on your router. The green light on the port will stay lit once it is seated at both ends.', 'For WiFi, select "WiFi" and follow the instructions on the app.', 'Enter the Serial Number and Security Key from the back of the Comm Link.', 'Once it comes back online, test the whole network. Press and release the Test button on a sensor and have someone watch the others respond.'],
        type: 'steps'
      }, 'Ethernet is the more reliable of the two if the Comm Link can sit near the router.'],
      continue: {
        label: 'I have a different problem',
        next: 'issue'
      },
      escalate: {
        label: 'Get help with the setup',
        reason: 'comm_link_setup'
      },
      resolution: 'self_service',
      title: 'Setting up the Comm Link again',
      type: 'document'
    },
    /*
     * Sign in problems, and the reason this branch is not just an
     * escalation.
     *
     * THE OLD APP HAD NO ACCOUNTS. It held a device token, so the phone was
     * the account: install it, add your network, done. The current app has
     * real user accounts, and a customer who has been with us for years
     * arrives at a login screen having never made one. They are not locked
     * out, they have nothing to be locked out of, and no amount of
     * password resetting will help.
     *
     * That is a five minute fix they can do themselves, and it was going
     * straight to a person. This asks the one question that separates the
     * three cases.
     */
    login: {
      help: 'The newest version of our app changed how accounts work, and the answer depends on which side of that you are on.',
      options: [{
        icon: 'person-badge',
        label: 'I used the old app and never made an account',
        next: 'doc_new_account'
      }, {
        icon: 'lock-fill',
        label: 'I have an account but forgot my password',
        next: 'doc_password'
      }, {
        icon: 'broadcast',
        label: 'I signed in but my sensors are not there',
        next: 'doc_attach_network'
      }, {
        icon: 'search',
        label: 'I am not sure which of those it is',
        next: 'doc_new_account'
      }, {
        icon: 'card-text',
        label: 'None of these describe my problem',
        next: 'esc_app_account'
      }],
      prompt: 'Have you created an account in the current app?',
      type: 'choice'
    },
    doc_new_account: {
      body: ['The app you had before did not have accounts. It recognized the phone itself, so installing it and adding your network was the whole of it, and there was never a username or a password to remember.', 'The current app uses proper accounts, which is what lets the same network appear on a partner\'s phone, survive a lost handset, and send alerts to more than one person. It also means that if you have not made one yet, there is nothing to sign in with. That is not a lost password. There is no account to recover.', {
        text: 'Your network is not affected by any of this. The sensors keep talking to each other and keep sounding whether or not anybody is signed in.',
        type: 'note'
      }, {
        items: ['On the login screen, choose to create an account. It is free, and it only requires your name and contact information to get started.', 'Once you are in, add your existing network with the Comm Link\'s Serial Number and Security Key. Both are printed on the label on the back of the Comm Link, and the letters in the Security Key must be entered in UPPER CASE.', 'Your sensors appear with the names they already had.'],
        type: 'steps'
      }, 'You are attaching the account to a network that already exists, not building it again. Nothing is unbound, nothing is re-paired, and no sensor comes off the ceiling.'],
      continue: {
        label: 'I have a different problem',
        next: 'app'
      },
      escalate: {
        label: 'I am stuck on one of these steps',
        reason: 'app_account'
      },
      resolution: 'self_service',
      title: 'Making an account for the first time',
      type: 'document'
    },
    doc_password: {
      body: ['If you have signed in on this app before, the account exists and the password can be reset without anybody helping.', {
        items: ['On the login screen, choose the option to reset your password.', 'Enter the mobile phone number the account was made with.', 'A code arrives by text message. Enter it, then choose a new password.'],
        type: 'steps'
      }, {
        text: 'If no text message arrives, make sure you are using the same mobile phone number the account was created with.',
        type: 'note'
      }, 'If you have never created an account in this version of the app, a reset will not find anything, because there is nothing to reset. That is the common case for anybody who used the older app, and it is covered separately.'],
      continue: {
        label: 'I never made an account',
        next: 'doc_new_account'
      },
      escalate: {
        label: 'The reset is not working',
        reason: 'app_account'
      },
      resolution: 'self_service',
      title: 'Resetting your password',
      type: 'document'
    },
    doc_attach_network: {
      body: ['A new account starts empty. Signing in does not find your sensors on its own, because nothing yet connects the account to your network. You attach it by registering the network, entering the serial/key printed on the Comm Link.', {
        items: ['Find the label on the back of the Comm Link. It carries a Serial Number and a Security Key.', 'In the app, add a network and enter both.', 'Enter the letters in the Security Key in UPPER CASE. It is the single most common reason this is rejected.'],
        type: 'steps'
      }, {
        text: 'This does not set your network up again. The sensors stay bound to each other and to the Comm Link exactly as they are; the account is simply given permission to see them.',
        type: 'note'
      }, 'If the Comm Link is offline the sensors will not update and emergency alert notifications will not be sent because these features rely on an internet connection rather than using the radio frequency the sensors use.'],
      continue: {
        label: 'My Comm Link is offline',
        next: 'doc_comm_offline'
      },
      escalate: {
        label: 'It will not accept my details',
        reason: 'app_account'
      },
      resolution: 'self_service',
      title: 'Adding your network to the app',
      type: 'document'
    },
    esc_app_account: {
      reason: 'app_account',
      summary: 'Anything left after making an account, resetting a password and attaching the network needs a person: the remaining causes all involve confirming who you are against the network before anything is changed.',
      title: 'Getting you back into the app',
      type: 'escalate'
    },
    esc_chirp: {
      reason: 'chirp_undiagnosed',
      summary: 'A chirp we cannot pin down from the light is worth a person looking at. Tell us what you can see and hear and we will work it out from there.',
      title: 'Let us work out what that chirp is',
      type: 'escalate'
    },
    esc_other: otherEscalation()
  }
};
export default omnishield;